import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import ProjectIntroScreen from './ProjectIntroScreen';
import NewProjectForm from './NewProjectForm';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Shell of the "פרויקט חדש" wizard: intro screen → 3-step form → success screen,
 * matching the designer's five variants.
 *
 * The form stays MOUNTED while the intro is showing (hidden via display, not unmounted), so
 * "חזרה להתחלה" from step 1 can go back and forth without discarding what was typed. Resetting
 * is therefore explicit: `formKey` is bumped only when a genuinely new project is started.
 *
 * Do NOT collapse this back to `hasStarted ? <NewProjectForm/> : <ProjectIntroScreen/>`. That
 * reads like ordinary conditional rendering, but it unmounts the form on every trip to the
 * intro and silently throws away everything the coordinator typed - and nothing in a build,
 * a typecheck or a unit test catches it, because the components themselves are fine.
 */
const NewProjectDialog = ({ open, onClose, onSuccess }: NewProjectDialogProps) => {
  const { t } = useTranslation();
  const [hasStarted, setHasStarted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleClose = () => {
    setHasStarted(false);
    setIsSuccess(false);
    // Remounting on close keeps a reopened dialog from showing the previous submission's data.
    setFormKey((key) => key + 1);
    onClose();
  };

  const handleSubmitted = () => {
    setIsSuccess(true);
    // Refresh the list behind the dialog now, so it is already up to date when the
    // coordinator returns to it from the success screen.
    onSuccess();
  };

  const handleAddAnother = () => {
    setIsSuccess(false);
    setHasStarted(false);
    setFormKey((key) => key + 1);
  };

  if (isSuccess) {
    return (
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) handleClose();
        }}
        title={t('projects.wizard.dialogTitle')}
      >
        <SuccessScreen
          title={t('projects.wizard.success.title')}
          description={t('projects.wizard.success.subtitle')}
          primaryButtonText={t('projects.wizard.success.backToList')}
          primaryButtonAction={handleClose}
          secondaryButtonText={t('projects.wizard.success.addAnother')}
          secondaryButtonAction={handleAddAnother}
        />
      </MainDialogLayout>
    );
  }

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={t('projects.wizard.dialogTitle')}
    >
      {!hasStarted && <ProjectIntroScreen onStart={() => setHasStarted(true)} />}
      {/* display:contents keeps the stepper's own flex layout intact - an extra flex item here
          would break the article/footer split. Inline styles so nothing can override them. */}
      <div style={hasStarted ? { display: 'contents' } : { display: 'none' }}>
        <NewProjectForm
          key={formKey}
          onSuccess={handleSubmitted}
          onBackToIntro={() => setHasStarted(false)}
        />
      </div>
    </MainDialogLayout>
  );
};

export default NewProjectDialog;
